/**
 * reportFormat.ts — display helpers for the programme outcome report (VER-109).
 *
 * Pure formatting only. The heavy lifting (t-test, Cohen's d, plain-language summary)
 * happens server-side in helpers/statistics.ts; here we just render its numbers in a
 * consistent, APA-flavoured style. No rounding decisions that change meaning — the
 * server is the source of truth, we only present.
 */
import { Descriptives, ProgrammeOutcomeReport } from "../../../client/api/survey";

const EMDASH = "—";

/**
 * Fallback floor, used only if a response predates the server-side suppression field.
 *
 * The authoritative floor now lives in the API (services/surveyReporting.ts) and arrives on
 * each report as `minimumReportableGroup`. Keeping a client copy is defence in depth, not the
 * control: the server withholds the figures, so a stale client can no longer expose them.
 */
export const MIN_REPORTABLE_GROUP = 3;

/** The floor to quote in the on-screen notice — the server's value where present. */
export const reportableGroupFloor = (report: ProgrammeOutcomeReport): number =>
    report.minimumReportableGroup ?? MIN_REPORTABLE_GROUP;

/**
 * Whether a scale's group results must be hidden for anonymity (too few paired participants).
 *
 * Suppresses when EITHER the server flagged it OR the paired count is below the floor —
 * deliberately not just the flag. The two should always agree; if they ever disagree the safe
 * reading wins, and an older response with no flag still renders the notice rather than a card
 * of unexplained em-dashes.
 */
export const isAnonymitySuppressed = (report: ProgrammeOutcomeReport): boolean =>
    report.suppressed === true || report.participantsPaired < reportableGroupFloor(report);

/** Fixed-dp number, or an em-dash when the value is null/undefined/non-finite. */
export const fmtNum = (value: number | null | undefined, dp = 1): string =>
    value === null || value === undefined || !Number.isFinite(value) ? EMDASH : value.toFixed(dp);

/**
 * APA-style p-value: "< .001" for very small p, otherwise ".NNN" with the leading
 * zero dropped. Null → em-dash.
 */
export const fmtP = (p: number | null | undefined): string => {
    if (p === null || p === undefined || !Number.isFinite(p)) return EMDASH;
    if (p < 0.001) return "< .001";
    return p.toFixed(3).replace(/^0/, "");
};

/** t statistic to 2 dp (can be negative). */
export const fmtT = (t: number | null | undefined): string => fmtNum(t, 2);

/** Cohen's d to 2 dp (can be negative). */
export const fmtD = (d: number | null | undefined): string => fmtNum(d, 2);

export type ChangeDirection = "improved" | "declined" | "unchanged" | "unknown";

/**
 * Direction of change in wellbeing terms, respecting scale polarity.
 * meanDiff = endpoint − baseline; on a lower-is-better scale a fall is an improvement.
 */
export const changeDirection = (report: ProgrammeOutcomeReport): ChangeDirection => {
    const diff = report.tTest.meanDiff;
    if (diff === null || diff === undefined) return "unknown";
    if (diff === 0) return "unchanged";
    const improved = report.higherIsBetter ? diff > 0 : diff < 0;
    return improved ? "improved" : "declined";
};

/** Whether the paired change reached the conventional α = .05 threshold. */
export const isSignificant = (report: ProgrammeOutcomeReport): boolean =>
    report.tTest.computable && report.tTest.p !== null && report.tTest.p < 0.05;

/**
 * On-screen descriptive rows. Deliberately EXCLUDES minimum and maximum: those are the
 * exact scores of specific individuals (the extremes) and rendering them risks
 * identifying a participant in a small group. Alison's "score range" requirement is met
 * by the Range row (max − min, a single spread number). The exact min/max survive in the
 * raw CSV export, which is the sanctioned place for individual-level data.
 */
export const descriptivesRows = (
    baseline: Descriptives,
    endpoint: Descriptives,
): { label: string; baseline: string; endpoint: string }[] => [
    { label: "Participants (n)", baseline: String(baseline.n), endpoint: String(endpoint.n) },
    { label: "Mean", baseline: fmtNum(baseline.mean), endpoint: fmtNum(endpoint.mean) },
    { label: "Std. deviation", baseline: fmtNum(baseline.sd, 2), endpoint: fmtNum(endpoint.sd, 2) },
    { label: "Score range", baseline: fmtNum(baseline.range), endpoint: fmtNum(endpoint.range) },
];
